import { useEffect } from "react";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import Icon from "common/components/Icon";
import errorImages from "common/constants/errorImages";
import { useStableCallback } from "common/hooks";
import { useSearchState } from "common/router/hooks";
import { Dialog } from "common/stackable/types";
import { parseNumber } from "common/utils/numbers";
import { WithStringValues } from "common/utils/types";

import Button, { ButtonProps, ButtonVariant } from "client/common/components/buttons/Button";
import Page from "client/common/components/layouts/Page";
import Subheader from "client/common/components/layouts/Subheader";
import Body from "client/common/components/layouts/Body";
import { TextChipProps } from "client/common/components/content/TextChip";
import Results from "client/common/components/results/Results";
import MobileSupportChatButton from "client/support/components/MobileSupportChatButton";
import { useBreakpoints } from "client/common/hooks";
import { Paths } from "client/routes";

import { useDosageRemindersQuery } from "../hooks";
import { DosageReminder, DosageReminderLookup, DosageRemindersPageSearch } from "../types";
import { useReminderDrawer, useReminderItemSelectDrawer } from "../stackables";
import ReminderCard from "./ReminderCard";

import styles from "../styles/DosageRemindersPage.module.less";

// TODO Make new colors for chips
const PATIENT_CHIP_VARIANTS: TextChipProps["variant"][] = ["default", "grey"];

const sortInactiveLast = (reminders: DosageReminder[]) =>
  [...reminders].sort((a, b) => Number(b.is_active) - Number(a.is_active));

const toReminderLookup = (
  search: WithStringValues<DosageRemindersPageSearch>,
): DosageReminderLookup | null => {
  const id = parseNumber(search.id, null);
  if (id !== null) {
    return { id };
  }
  const itemId = parseNumber(search.item, null);
  const patientId = parseNumber(search.patient, null);
  if (itemId !== null && patientId !== null) {
    return { item_id: itemId, patient_id: patientId };
  }
  return null;
};

/** Opens the reminder drawer for the reminder in the URL, and clears it from the URL on close */
const useReminderDrawerSearch = () => {
  const [{ id, item, patient }, setSearch] = useSearchState<Paths["dosageReminders"]["list"]>({});
  const reminderDrawer = useStableCallback(useReminderDrawer());

  // Opening is keyed on the lookup params so rerenders don't stack duplicate drawers;
  // useDrawer ignores strict mode's repeat open
  useEffect(() => {
    const lookup = toReminderLookup({ id, item, patient });
    if (!lookup) {
      return;
    }
    reminderDrawer({
      lookup,
      onClose: () => setSearch({ id: undefined, item: undefined, patient: undefined }),
    }).open();
  }, [id, item, patient, reminderDrawer, setSearch]);
};

type RenderCreateButtonParams = {
  dialog: Dialog;
  variant: ButtonVariant;
  size?: ButtonProps["size"];
};

const renderCreateButton = ({ dialog, variant, size }: RenderCreateButtonParams) => (
  <Button variant={variant} size={size} className={styles.createButton} dialog={dialog}>
    <Icon icon={faPlus} /> Add reminder
  </Button>
);

const DosageRemindersPage = () => {
  const { data, isLoading, error } = useDosageRemindersQuery();
  const { isTabletPortraitAndSmaller } = useBreakpoints();
  const reminderItemSelectDrawer = useReminderItemSelectDrawer();
  useReminderDrawerSearch();

  const dosageReminders: DosageReminder[] | undefined = data && sortInactiveLast(data);
  const isEmpty = dosageReminders?.length === 0 && !error;

  const sortedPatientIds = [
    ...new Set(dosageReminders?.map((reminder) => reminder.patient.id)),
  ].sort((a, b) => a - b);

  const chipVariantByPatientId = new Map(
    sortedPatientIds.map((patientId, index) => [
      patientId,
      PATIENT_CHIP_VARIANTS[index % PATIENT_CHIP_VARIANTS.length],
    ]),
  );

  return (
    // The sticky create bar replaces the default footer, which renders its own support chat button
    <Page className={styles.page} Footer={isTabletPortraitAndSmaller ? null : undefined}>
      <Subheader
        title="Dosage Reminders"
        description="Schedule a text or email for when your pet's next dose is due"
        renderActions={() =>
          renderCreateButton({
            dialog: reminderItemSelectDrawer(),
            variant: "primary",
            size: "s",
          })
        }
        classNames={{
          container: styles.subheader,
          actions: styles.headerActions,
        }}
      />
      <Body className={styles.body}>
        {isLoading && <Results.Loading description="Loading dosage reminders" />}
        {!isLoading && (!dosageReminders || error) && (
          <Results.FetchError entityName="dosage reminders" error={error} isPlural={true} />
        )}
        {isEmpty && (
          <Results.None
            image={errorImages.EmptyAutoships}
            header="You don't have any dosage reminders yet"
            description="Add a reminder to get a text or email when your pet's next dose is due"
          />
        )}
        {!!dosageReminders?.length && !error && (
          <div className={styles.list}>
            {dosageReminders.map((reminder) => (
              <ReminderCard
                key={reminder.id}
                reminder={reminder}
                patientChipVariant={chipVariantByPatientId.get(reminder.patient.id) ?? null}
              />
            ))}
          </div>
        )}
      </Body>
      <div className={styles.createBar}>
        {renderCreateButton({
          dialog: reminderItemSelectDrawer(),
          variant: "secondary",
        })}
      </div>
      {isTabletPortraitAndSmaller && <MobileSupportChatButton />}
    </Page>
  );
};

export default DosageRemindersPage;
